/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { Coachmark } from '@fluentui/react/lib/Coachmark';
import { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { TeachingBubbleContent } from '@fluentui/react/lib/TeachingBubble';
import { TooltipDelay } from '@fluentui/react/lib/Tooltip';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { HighContrastSelector } from '@fluentui/style-utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getRBACTooltipStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { tryParseJson } from 'src/core/utils/base';
import { getStorage } from 'src/core/utils/getStorage';
import { globalMessages } from 'src/core/utils/globalMessages';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DeepStackTrainingModal } from 'src/luis/appShell/appPage/components/DeepStackTrainingModal';

export type LuisAppTrainButtonProps = {
    /**
     * Indicates if the app version needs training or not.
     */
    isVersionDirty: boolean;

    /**
     * Is training in progress.
     */
    isTrainingInProgress: boolean;

    /**
     * A boolean value indicates that training should be disabled
     * when some intents has no utterances.
     */
    isDisabled: boolean;

    /**
     * A call which will be invoked whenever the training is required.
     */
    onTrain: () => void;
};

const messages = defineMessages({
    train: {
        id: 'luis.luisAppTrainButton.train',
        defaultMessage: 'Train'
    },
    training: {
        id: 'luis.luisAppTrainButton.training',
        defaultMessage: 'Training ...'
    },
    upToDate: {
        id: 'luis.luisAppTrainButton.upToDate',
        defaultMessage: 'App up to date'
    },
    untrainedChanges: {
        id: 'luis.luisAppTrainButton.untrainedChanges',
        defaultMessage: 'App has untrained changes'
    },
    intentHasNoUtterance: {
        id: 'luis.luisAppTrainButton.intentHasNoUtterance',
        defaultMessage: 'Some intents have no utterances or patterns.'
    },
    trainNeuralModel: {
        id: 'luis.luisAppTrainButton.trainNeuralModel',
        defaultMessage: 'Train neural model'
    },
    tryNewTrainingNote: {
        id: 'luis.luisAppTrainButton.tryNewTrainingNote',
        defaultMessage: 'Try our new Neural model training preview feature.'
    }
});

const theme = getScopedTheme('LuisAppTrainButton');

/**
 * Displays the global train button.
 *
 * @param props The input properties for the train button.
 */
export const LuisAppTrainButton = observer((props: LuisAppTrainButtonProps) => {
    const { onTrain, isTrainingInProgress, isVersionDirty, isDisabled } = props;

    const coachMarkRef = React.useRef(getId('trainingCoachMark'));
    const [isCoachMarkHidden, setIsCoachMarkHidden] = React.useState(true);
    const [isDeepStackTrainingModelHidden, setIsDeepStackTrainingModelHidden] = React.useState(true);

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    React.useEffect(() => {
        if (isDeepStackTrainingAvailable) {
            const storage = getStorage('localStorage');
            setIsCoachMarkHidden(tryParseJson(storage.getData(LuisConstants.StorageKeys.NeuralModelTrainingCoachMarkStorageKey)) ?? false);
        }
    }, []);

    const currentApp = rootStore.currentLuisAppStore;
    const csAccount = rootStore.azureStore.session.csAccountStore;
    const isDeepStackTrainingAvailable =
        csAccount.featuresSettings.deepStackTraining &&
        LuisConstants.Luis.Cultures.NeuralModelTrainingSupportedCultures.includes(currentApp.culture);

    const trainBtnId = getId();
    const trainingOptions: IContextualMenuProps = isDeepStackTrainingAvailable && {
        items: [
            {
                key: 'deepStackTraining',
                text: localize(messages.trainNeuralModel),
                iconProps: { iconName: 'StackIndicator' },
                onClick: () => setIsDeepStackTrainingModelHidden(false)
            }
        ]
    };
    const trainBtnToolTipMessage = isDisabled
        ? canWrite
            ? messages.intentHasNoUtterance
            : globalMessages.cannotPerformAction
        : isVersionDirty
        ? messages.untrainedChanges
        : messages.upToDate;

    const indicatorStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
        root: { fontWeight: 'bold', color: isVersionDirty ? theme.redIndicator : theme.greenIndicator }
    });

    const buttonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
        root: { selectors: { [HighContrastSelector]: { border: isVersionDirty ? '1px dashed' : '1px solid' } } }
    });

    const dismissCoachMark = () => {
        getStorage('localStorage').setData(LuisConstants.StorageKeys.NeuralModelTrainingCoachMarkStorageKey, 'true');
        setIsCoachMarkHidden(true);
    };

    return (
        <LuisTooltip
            id={trainBtnId}
            delay={TooltipDelay.zero}
            content={localize(trainBtnToolTipMessage, { displayedUserRole })}
            calloutProps={{ directionalHint: DirectionalHint.bottomAutoEdge }}
            styles={!canWrite && getRBACTooltipStyles()}
            data-automation-id="TrainButtonLuisToolTip">
            <DefaultButton
                onClick={onTrain}
                styles={buttonStyles}
                menuProps={trainingOptions}
                aria-describedby={trainBtnId}
                data-automation-id="TrainButton"
                split={isDeepStackTrainingAvailable}
                disabled={isDisabled || !isVersionDirty}
                splitButtonMenuProps={{ id: coachMarkRef.current }}
                iconProps={{ iconName: 'Processing', styles: indicatorStyles }}>
                {localize(isTrainingInProgress ? messages.training : messages.train)}
            </DefaultButton>

            {!isCoachMarkHidden && (
                <Coachmark persistentBeak target={`#${coachMarkRef.current}`} onDismiss={dismissCoachMark}>
                    <TeachingBubbleContent hasCloseButton onDismiss={dismissCoachMark}>
                        {localize(messages.tryNewTrainingNote)}
                    </TeachingBubbleContent>
                </Coachmark>
            )}

            <DeepStackTrainingModal
                hidden={isDeepStackTrainingModelHidden}
                onSubmit={() => setIsDeepStackTrainingModelHidden(true)}
                onDismiss={() => setIsDeepStackTrainingModelHidden(true)}
            />
        </LuisTooltip>
    );
});
